import flow from 'lodash.flow';
import isEqual from 'lodash.isequal';

/**
 * @description Given an object and additional cleaning functions, return the object with all strings trimmed and empty strings turned to `null` as well as any string transformations done by the addional cleaning functions.
 * @param {object} object - The object to be deep cleaned.
 * @param {object} additionalCleaners - Additional cleaning functions to apply to each value in the object of a certain type.
 * @return {object} The cleaned object.
 */
function deepClean(object) {
  var additionalCleaners = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  function clean(_key, value) {
    if (typeof value === 'string') {
      value = value.trim();
      var stringCleaners = additionalCleaners.stringCleaners;
      if (Array.isArray(stringCleaners) && stringCleaners.length > 0) {
        value = flow(stringCleaners)(value);
      }
    }
    if (value === '') {
      return null;
    }
    return value;
  }
  if (!(object instanceof Object)) {
    throw new Error('The parameter provided is not an object.  Please pass an object.');
  }
  object = JSON.stringify(object, clean);
  return JSON.parse(object);
}

function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread2(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}
function _defineProperty(obj, key, value) {
  key = _toPropertyKey(key);
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _toPrimitive(input, hint) {
  if (typeof input !== "object" || input === null) return input;
  var prim = input[Symbol.toPrimitive];
  if (prim !== undefined) {
    var res = prim.call(input, hint || "default");
    if (typeof res !== "object") return res;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return (hint === "string" ? String : Number)(input);
}
function _toPropertyKey(arg) {
  var key = _toPrimitive(arg, "string");
  return typeof key === "symbol" ? key : String(key);
}

/**
 * @description Given two objects, finds the changes made from oldObject to newObject.  The optional keyBundle will add all keys in keyBundle to the returned diff if any of the keys in keyBundle has changed.
 * @param {object} oldObject - The old version of the object to be used as the basis for comparison.
 * @param {object} newObject - The new version of the object to be compared to oldObject.
 * @param {string[]} keyBundle - An array of keys that should always be included in the returned diff if any of the keys has changed.
 * @return {object} The difference between oldObject and newObject.
 */
function getDiff(oldObject, newObject, keyBundle) {
  var addKeyBundle = false;
  var diffObject = Object.keys(newObject).reduce(function (diff, key) {
    if (!isEqual(oldObject[key], newObject[key])) {
      diff = _objectSpread2(_objectSpread2({}, diff), {}, _defineProperty({}, key, newObject[key]));
      if (keyBundle && keyBundle.includes(key)) {
        addKeyBundle = true;
      }
    }
    return diff;
  }, {});
  if (addKeyBundle) {
    diffObject = keyBundle.reduce(function (diff, key) {
      return _objectSpread2(_objectSpread2({}, diff), {}, _defineProperty({}, key, newObject[key]));
    }, diffObject);
  }
  return diffObject;
}

/**
 * @description Given an object and an array of keys, return the first value from the list of keys that is truthy.
 * @param {object} object - The object to be scanned for truthy values.
 * @param {string[]} keys - The keys to iterate through.
 * @return {*} The first truthy value in the object, otherwise null.
 */
function getFirstTruthyValue(object, keys) {
  if (!(object instanceof Object)) {
    throw new Error('The parameter provided is not an object.  Please pass an object.');
  }
  if (!keys.every(function (key) {
    return typeof key === 'string';
  })) {
    throw new Error('The provided keys must be strings.  Please an array of strings.');
  }
  for (var i = 0; i < keys.length; i++) {
    if (object[keys[i]]) {
      return object[keys[i]];
    }
  }
  return null;
}

/**
 * @description Given `obj`, determine if it has been approved.
 * @param {object} obj - The object to be determined as approved.
 * @return {boolean} Whether the given object has been approved.
 */
function isApproved(obj) {
  return (obj === null || obj === void 0 ? void 0 : obj.approved) === true;
}

/**
 * @description Given `obj`, determine if it has been confirmed.
 * @param {object} obj - The object to be determined as confirmed.
 * @return {boolean} Whether the given object has been confirmed.
 */
function isConfirmed(obj) {
  return (obj === null || obj === void 0 ? void 0 : obj.confirmed) === true;
}

/**
 * @description Given `obj`, determine if it has not been approved.
 * @param {object} obj - The object to be determined as not approved.
 * @return {boolean} Whether the given object has not been approved.
 */
function isNotApproved(obj) {
  return (obj === null || obj === void 0 ? void 0 : obj.approved) === false;
}

/**
 * @description Given `obj`, determine if it has not been confirmed.
 * @param {object} obj - The object to be determined as not confirmed.
 * @return {boolean} Whether the given object has not been confirmed.
 */
function isNotConfirmed(obj) {
  return (obj === null || obj === void 0 ? void 0 : obj.confirmed) === false;
}

export { deepClean, getDiff, getFirstTruthyValue, isApproved, isConfirmed, isNotApproved, isNotConfirmed };
